/**
 * NERC CIP-002 inventory (point 3): labels and the CIP-002 rules the forms check before saving.
 * The backend (NercInventoryRules) is the authority; these mirror it so a form can say what is
 * wrong before the request, and the backend's message is shown whenever it still refuses.
 */

export const SITE_TYPES = [
  { value: "ControlCentre", label: "Control centre" },
  { value: "BackupControlCentre", label: "Backup control centre" },
  { value: "Substation", label: "Substation" },
  { value: "Generation", label: "Generation plant" },
  { value: "DataCentre", label: "Data centre" },
  { value: "Other", label: "Other" },
];

export const siteTypeLabel = (value) => SITE_TYPES.find((t) => t.value === value)?.label || value;

export const IMPACTS = ["High", "Medium", "Low"];

const range = (section, count) => Array.from({ length: count }, (_, i) => `${section}.${i + 1}`);

/** CIP-002 Attachment 1 criteria by impact: High 1.1–1.4, Medium 2.1–2.13, Low 3.1–3.6. */
export const CRITERIA = {
  High: range(1, 4),
  Medium: range(2, 13),
  Low: range(3, 6),
};

export const impactForCriterion = (criterion) =>
  IMPACTS.find((impact) => CRITERIA[impact].includes((criterion || "").trim())) || null;

export const ASSET_CLASSES = [
  { value: "BCA", label: "BCA", title: "BES Cyber Asset" },
  { value: "EACMS", label: "EACMS", title: "Electronic Access Control or Monitoring System" },
  { value: "PACS", label: "PACS", title: "Physical Access Control System" },
  { value: "PCA", label: "PCA", title: "Protected Cyber Asset" },
];

export const IMPACT_TONES = {
  High: "bg-red-50 text-red-700 border-red-200",
  Medium: "bg-orange-50 text-orange-700 border-orange-200",
  Low: "bg-yellow-50 text-yellow-700 border-yellow-200",
};

export const isHighOrMedium = (impact) => impact === "High" || impact === "Medium";

/**
 * The Cyber Asset form's checks (backend NercInventoryRules.ValidateCyberAsset): at least one
 * system; EACMS / PACS / PCA only for High or Medium systems; a PCA inside an ESP.
 */
export function cyberAssetProblems({ classification, systemIds, espId }, systemsById) {
  const problems = [];
  const systems = (systemIds || []).map((id) => systemsById[id]).filter(Boolean);
  if (systems.length === 0) problems.push("Choose at least one BES Cyber System.");
  if (classification && classification !== "BCA" && systems.some((s) => !isHighOrMedium(s.impact)))
    problems.push(`${classification} applies only to High or Medium impact systems; a Low impact system's Cyber Assets are BCAs.`);
  if (classification === "PCA" && !espId) problems.push("A Protected Cyber Asset (PCA) must be inside an Electronic Security Perimeter.");
  return problems;
}

export const CLOCK_LABELS = {
  NeverRecorded: { text: "Never recorded", tone: "bg-gray-100 text-gray-700 border-gray-200" },
  Overdue: { text: "Overdue", tone: "bg-red-50 text-red-700 border-red-200" },
  DueSoon: { text: "Due soon", tone: "bg-orange-50 text-orange-700 border-orange-200" },
  Current: { text: "Current", tone: "bg-green-50 text-green-700 border-green-200" },
};

/** "2027-04-30" → "30 Apr 2027" (a date-only value: no time zone shift). */
export function formatDateOnly(value) {
  if (!value) return "—";
  const [y, m, d] = String(value).split("-").map(Number);
  if (!y || !m || !d) return String(value);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

export const todayIso = () => new Date().toISOString().slice(0, 10);

export const userLabel = (u) => u?.fullName || u?.email || `User ${u?.id}`;

/** Saves a blob the API returned as a file. */
export function saveBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

/** The backend's message for a failed request, or the fallback. */
export const apiError = (err, fallback) => err?.response?.data?.message || fallback;
